import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useBottomSheet - Hook для bottom sheet с поддержкой скролла контента
 *
 * Логика:
 * - Если контент прокручен (scrollTop > 0), touch события идут на скролл контента
 * - Если контент на самом верху (scrollTop === 0) и свайп ВНИЗ → двигаем модалку
 * - При отпускании: velocity > threshold ИЛИ distance > 100px = закрываем
 * - Иначе возвращаем на место
 */
export function useBottomSheet({
  onDismiss,
  isOpen = false
}) {
  const [isDragging, setIsDragging] = useState(false);

  // Refs для drag без re-render
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const startTime = useRef(0);
  // Режим: null (не определён), 'drag' (двигаем sheet), 'scroll' (скроллим контент)
  const modeRef = useRef(null);

  // Найти scrollable контейнер (.sheet-content) внутри sheet
  const getScrollableContent = useCallback(() => {
    if (!sheetRef.current) return null;
    return sheetRef.current.querySelector('.sheet-content');
  }, []);

  // Dismiss с анимацией (slide-down на mobile, fade-out на desktop)
  const dismiss = useCallback(() => {
    if (sheetRef.current) {
      const isDesktop = window.innerWidth >= 768;
      sheetRef.current.style.animation = 'none';
      sheetRef.current.classList.remove('dragging');

      if (isDesktop) {
        sheetRef.current.style.transition = 'transform 0.2s ease-in, opacity 0.2s ease-in';
        sheetRef.current.style.transform = 'scale(0.95)';
        sheetRef.current.style.opacity = '0';
      } else {
        sheetRef.current.style.transition = 'transform 0.25s ease-out';
        sheetRef.current.style.transform = 'translateY(100%)';
      }

      setTimeout(() => {
        if (sheetRef.current) sheetRef.current.style.opacity = '';
        onDismiss?.();
      }, isDesktop ? 200 : 250);
    } else {
      onDismiss?.();
    }
  }, [onDismiss]);

  // Snap back к исходной позиции
  const snapBack = useCallback(() => {
    if (sheetRef.current) {
      sheetRef.current.style.animation = 'none';
      sheetRef.current.classList.remove('dragging');
      sheetRef.current.style.transition = 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)';
      sheetRef.current.style.transform = 'translateY(0)';
    }
  }, []);

  // Touch Start
  const handleTouchStart = useCallback((e) => {
    if (!sheetRef.current) return;

    startY.current = e.touches[0].clientY;
    currentY.current = 0;
    startTime.current = Date.now();
    // Режим определится на первом touchMove
    modeRef.current = null;
  }, []);

  // Touch Move - определяем режим на первом move, затем действуем
  const handleTouchMove = useCallback((e) => {
    if (!sheetRef.current || startY.current === 0) return;

    const touchY = e.touches[0].clientY;
    const deltaY = touchY - startY.current;

    // На первом move определяем режим
    if (modeRef.current === null) {
      const scrollable = getScrollableContent();
      const scrollTop = scrollable ? scrollable.scrollTop : 0;

      // Свайп вниз + контент на самом верху → drag mode
      if (deltaY > 0 && scrollTop <= 0) {
        modeRef.current = 'drag';
      } else {
        // Свайп вверх ИЛИ контент прокручен → scroll mode
        modeRef.current = 'scroll';
      }
    }

    // В режиме scroll — не вмешиваемся, нативный скролл работает
    if (modeRef.current === 'scroll') {
      return;
    }

    // Режим drag — двигаем sheet за пальцем
    e.preventDefault();

    let adjustedDelta = deltaY;
    // Сопротивление при движении вверх (тянут модалку выше исходной позиции)
    if (adjustedDelta < 0) {
      adjustedDelta = adjustedDelta * 0.3;
    }

    currentY.current = adjustedDelta;

    // Включаем drag-стили при первом drag move
    if (!sheetRef.current.classList.contains('dragging')) {
      sheetRef.current.classList.add('dragging');
      sheetRef.current.style.transition = 'none';
    }

    setIsDragging(true);

    // Обновляем DOM напрямую — мгновенный отклик
    sheetRef.current.style.transform = `translateY(${adjustedDelta}px)`;
  }, [getScrollableContent]);

  // Touch End - определяем dismiss или snap back
  const handleTouchEnd = useCallback(() => {
    if (!sheetRef.current) return;

    setIsDragging(false);

    // Если были в scroll режиме — ничего не делаем
    if (modeRef.current !== 'drag') {
      modeRef.current = null;
      startY.current = 0;
      currentY.current = 0;
      return;
    }

    const deltaY = currentY.current;
    const deltaTime = Date.now() - startTime.current;
    const velocity = deltaY / deltaTime; // px/ms

    // Условия для dismiss:
    // 1. Быстрый свайп вниз (velocity > 0.5 px/ms)
    // 2. Или свайп больше 100px вниз
    const shouldDismiss = velocity > 0.5 || deltaY > 100;

    if (shouldDismiss && deltaY > 0) {
      dismiss();
    } else {
      snapBack();
    }

    // Сброс
    modeRef.current = null;
    startY.current = 0;
    currentY.current = 0;
  }, [dismiss, snapBack]);

  // При открытии - сброс стилей
  useEffect(() => {
    if (isOpen && sheetRef.current) {
      sheetRef.current.style.transform = 'translateY(0)';
      sheetRef.current.style.transition = '';
      sheetRef.current.classList.remove('dragging');
    }
  }, [isOpen]);

  // Backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      dismiss();
    }
  }, [dismiss]);

  return {
    // State
    isDragging,

    // Ref для элемента
    sheetRef,

    // Handlers для touch
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd
    },

    // Actions
    dismiss,
    handleBackdropClick
  };
}

export default useBottomSheet;
